// Globals
import { TFunction } from 'react-i18next';

// Models
import { IOrder, IOrderDetails, OrdersStatus } from '@/models/orders.model';

// Components
import { StyledBadge, StyledIndicator, StyledText, StyledAttentionIcon } from './OrdersStatusBadgeStyles';
import { Tooltip } from '@/ui';
import { AttentionIcon } from '@/icons';

// Helpers
import { getStatusData, getPaymentStatusData, isAwaitingOverdue, getAwaitingDateFromHistory } from '../../helpers';
import { formatDate } from '@/utils';
import { DateFormats } from '@/constants/dates';

interface OrderStatusBadgeProps {
  t: TFunction;
  record: IOrder | IOrderDetails;
  isPaymentStatus?: boolean;
  wrap?: boolean;
}

/**
 * Type guard to check if record is IOrder (from orders list).
 * IOrder has 'awaiting_since' field directly from the list API.
 */
const isOrderFromList = (record: IOrder | IOrderDetails): record is IOrder => {
  return 'awaiting_since' in record;
};

/**
 * Type guard to check if record is IOrderDetails (from order details API).
 * IOrderDetails has 'status_history' array instead of 'awaiting_since'.
 */
const isOrderDetails = (record: IOrder | IOrderDetails): record is IOrderDetails => {
  return 'status_history' in record;
};

/**
 * Extracts the awaiting date based on record type.
 *
 * Different API endpoints return awaiting date differently:
 * - GET /orders (list) → returns 'awaiting_since' field directly
 * - GET /orders/:id (details) → returns 'status_history' array, need to extract date
 */
const extractAwaitingSinceDate = (record: IOrder | IOrderDetails): string | null => {
  if (isOrderFromList(record)) {
    return record.awaiting_since;
  }

  if (isOrderDetails(record)) {
    return getAwaitingDateFromHistory(record.status_history);
  }

  return null;
};

/**
 * Gets the delivery type from the record, handling both IOrder and IOrderDetails structures.
 */
const getDeliveryType = (record: IOrder | IOrderDetails) => {
  if (isOrderFromList(record)) {
    return record.delivery_type;
  }
  return record?.delivery?.delivery;
};

const OrderStatusBadge = ({ t, record, isPaymentStatus, wrap }: OrderStatusBadgeProps) => {
  const statusData = isPaymentStatus
    ? getPaymentStatusData(record.payment_status)
    : getStatusData(record.status, getDeliveryType(record));

  const awaitingSince = extractAwaitingSinceDate(record);
  const isOverdue = !isPaymentStatus && record.status === OrdersStatus.AWAITING && isAwaitingOverdue(awaitingSince);

  const badge = (
    <StyledBadge $isOverdue={isOverdue} $wrap={wrap}>
      {isOverdue ? (
        <StyledAttentionIcon $wrap={wrap}>
          <AttentionIcon />
        </StyledAttentionIcon>
      ) : (
        <StyledIndicator $color={statusData.color} $wrap={wrap} />
      )}
      <StyledText $isOverdue={isOverdue} $wrap={wrap}>
        {t(statusData.text)}
      </StyledText>
    </StyledBadge>
  );

  if (isOverdue && awaitingSince) {
    const formattedDate = formatDate(awaitingSince, DateFormats.EUROPEAN_DEFAULT);
    return <Tooltip title={`Очікує з ${formattedDate}`}>{badge}</Tooltip>;
  }

  return badge;
};

export default OrderStatusBadge;
